import { TimeFrame, Sorting } from "../types";

export enum ViewMode {
  Site,
  Responsible,
}

// Keep values equivalent to API keys
export enum MetricsType {
  Opened = "opened",
  Completed = "completed",
}

// Keep values equivalent to API keys
export enum MetricsByType {
  BySite = "bySite",
  ByResponsible = "byResponsible",
}

export interface GroupedMetrics {
  [key: string]: {
    count: number;
    critical: number;
    breakdown: {
      [key: string]: {
        name: string;
        count: number;
        critical: number;
        actionIds?: string[];
      };
    };
  };
}

interface DetailedSummary {
  [key: string]: {
    name: string;
    [MetricsType.Completed]: number;
    [MetricsType.Opened]: number;
  };
}

export interface Summary {
  [MetricsType.Completed]: number;
  [MetricsType.Opened]: number;
  [MetricsByType.BySite]: DetailedSummary;
  [MetricsByType.ByResponsible]: DetailedSummary; // @TODO: currently missing this in API response
}

export interface Metrics {
  total: number;
  [MetricsByType.BySite]: GroupedMetrics;
  [MetricsByType.ByResponsible]: GroupedMetrics;
}

export interface ComposedMetricsSite {
  siteId: string;
  siteName: string;
  fill: number;
  count: number;
}

interface LoadingState {
  summaryLoading: boolean;
  summaryLoaded: boolean;
  shouldUpdateSummary: boolean;
  metricsLoading: boolean;
  metricsLoaded: boolean;
  shouldUpdateMetrics: boolean;
}

interface TimeFrameState {
  timeFrameOption: TimeFrame;
  startDate: string;
  endDate: string;
}

interface SortingState {
  sortingDirection: Sorting;
}

interface SiteState {
  selectedSites: ComposedMetricsSite[];
  sites: Map<string, ComposedMetricsSite>;
  active: Set<string>;
}

interface ByType {
  byType: MetricsByType;
}

interface SummaryState {
  summary: Summary;
}

export interface MetricsState {
  metrics: {
    currentMetricsType: MetricsType;
    pinnedResponsibleList: string[];
    [MetricsType.Completed]: Metrics;
    [MetricsType.Opened]: Metrics;
  };
}

export type SummaryRequestSuccess = {
  metricsType: MetricsType;
  sites: string[];
} & Summary;
export type State = LoadingState &
  TimeFrameState &
  SortingState &
  SiteState &
  SummaryState &
  MetricsState &
  ByType;

type LoadSummary = { type: "LOAD_SUMMARY" };
type LoadMetrics = { type: "LOAD_METRICS" };
type SummaryRequestSuccessAction = {
  type: "SUMMARY_REQUEST_SUCCESS";
} & SummaryRequestSuccess;
type SummaryRequestFailAction = { type: "SUMMARY_REQUEST_FAIL" };
type MetricsRequestSuccess = { type: "METRICS_REQUEST_SUCCESS" } & Metrics & {
    metricsType: MetricsType;
  };
type MetricsRequestFail = { type: "METRICS_REQUEST_FAIL" };
type SetShouldUpdateSummary = {
  type: "SET_SHOULD_UPDATE_SUMMARY";
  shouldUpdate: boolean;
};
type SetShouldUpdateMetrics = {
  type: "SET_SHOULD_UPDATE_METRICS";
  shouldUpdate: boolean;
};
type UpdateTimeFrame = { type: "SET_TIME_FRAME" } & TimeFrameState;
type SetSortingDirection = { type: "SET_SORTING_DIRECTION" } & SortingState;
type SetSelectedSitesList = {
  type: "SET_SELECTED_SITES_LIST";
  active: Set<string>;
};
type SetSitesList = { type: "SET_SITES_LIST"; sites: ComposedMetricsSite[] };
type AddResponsible = { type: "ADD_RESPONSIBLE"; responsible: string };
type RemoveResponsible = { type: "REMOVE_RESPONSIBLE"; responsible: string };
type UpdateSelectedSitesList = {
  type: "UPDATE_SELECTED_SITES_LIST";
  site: ComposedMetricsSite;
};
type SetMetricsByType = { type: "SET_METRICS_BY_TYPE"; by: MetricsByType };

export type Action =
  | LoadSummary
  | LoadMetrics
  | SummaryRequestSuccessAction
  | SummaryRequestFailAction
  | MetricsRequestSuccess
  | MetricsRequestFail
  | SetShouldUpdateSummary
  | SetShouldUpdateMetrics
  | UpdateTimeFrame
  | SetSortingDirection
  | SetSelectedSitesList
  | SetSitesList
  | AddResponsible
  | RemoveResponsible
  | UpdateSelectedSitesList
  | SetMetricsByType;
